import axios from "axios";
import { getToken, clearSession } from "./session";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

// Derive just the host (e.g. "api-insights.haqdarshak.com") from the configured
// API base URL so we can recognise requests going to our own API regardless of
// how the URL was built (relative path vs absolute, trailing slash, etc).
let API_HOST = "";
try {
  API_HOST = new URL(API_BASE_URL).host;
} catch {
  API_HOST = "";
}

// Endpoints that must NOT trigger the forced-logout-and-redirect behavior on
// a 401 response — a 401 from these just means "not logged in yet / bad
// credentials", which the caller already handles explicitly.
const EXEMPT_PATHS = ["google-login", "auth/csrf-token", "auth/verify"];

const isApiUrl = (url) => {
  if (!url || !API_HOST) return false;
  try {
    // Resolve relative URLs against the current origin so `new URL` doesn't throw.
    const resolved = new URL(url, window.location.origin);
    return resolved.host === API_HOST;
  } catch {
    return false;
  }
};

const isExemptUrl = (url) => {
  if (!url) return false;
  return EXEMPT_PATHS.some((path) => url.includes(path));
};

const handleUnauthorized = (url) => {
  if (!isApiUrl(url) || isExemptUrl(url)) return;
  clearSession();
  if (window.location.pathname !== "/") {
    window.location.replace("/");
  }
};

// ---------------------------------------------------------------------------
// Patch window.fetch
// ---------------------------------------------------------------------------
const originalFetch = window.fetch.bind(window);

window.fetch = async (input, init = {}) => {
  const url = typeof input === "string" || input instanceof URL ? String(input) : input?.url;

  let finalInit = init;
  if (isApiUrl(url)) {
    const token = getToken();
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    finalInit = { ...init, headers };
  }

  const response = await originalFetch(input, finalInit);

  if (response.status === 401) {
    handleUnauthorized(url);
  }

  return response;
};

// ---------------------------------------------------------------------------
// Patch axios (used directly e.g. in src/components/InfoPopup.js)
// ---------------------------------------------------------------------------
axios.interceptors.request.use((config) => {
  const url = config.url ? new URL(config.url, config.baseURL || window.location.origin).toString() : "";
  if (isApiUrl(url)) {
    const token = getToken();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error?.config;
    const url = config?.url
      ? new URL(config.url, config.baseURL || window.location.origin).toString()
      : "";
    if (error?.response?.status === 401) {
      handleUnauthorized(url);
    }
    return Promise.reject(error);
  }
);
